import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import { searchBanques } from '@/routes';
import type { ApiResponseType, BanqueType } from '@/types';

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_DELAY = 300;

type BanqueOption = {
  key: number;
  value: string;
  text: string;
};

const useBanqueSearch = () => {
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState<BanqueOption[]>([]);

  const abortControllerRef = React.useRef<AbortController | null>(null);

  const fetchBanques = React.useCallback((query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    request
      .get(searchBanques({ query, perPage: 10 }), { signal: controller.signal })
      .then(({ data }: ApiResponseType<BanqueType>) => {
        const actives = _.filter(data, { caduque: 0 });

        setOptions(
          _.map(actives, (banque) => ({
            key: banque.identifiant,
            value: banque.nom,
            text: banque.nom,
          })),
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          setOptions([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
  }, []);

  const debouncedFetch = React.useRef(
    _.debounce((query: string) => fetchBanques(query), DEBOUNCE_DELAY),
  );

  React.useEffect(
    () => () => {
      debouncedFetch.current.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    [],
  );

  const onSearchChange = React.useCallback(
    (e: React.SyntheticEvent, { searchQuery }: { searchQuery: string }) => {
      debouncedFetch.current(searchQuery);
    },
    [],
  );

  return { loading, options, onSearchChange };
};

export default useBanqueSearch;
